import { css, StyleSheet } from 'aphrodite';
import type { Components } from 'react-markdown';
import type { TextRenderer } from './types';

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;

/**
 * Custom code component that renders hex colors with visual swatches.
 * When the code content is a valid hex color (e.g., #0033A1), displays
 * a small colored square before the hex code.
 */
const ColorSwatchCode: Components['code'] = ({ node, className, children, ...props }) => {
	const inline = node?.position?.start.line === node?.position?.end.line;

	if (!inline) {
		return (
			<code className={className} {...props}>
				{children}
			</code>
		);
	}

	const content = String(children).trim();
	const isHexColor = HEX_COLOR_REGEX.test(content);

	if (isHexColor) {
		return (
			<code className={css(s.colorCode)} {...props}>
				<span className={css(s.colorSwatch)} style={{ backgroundColor: content }} />
				{children}
			</code>
		);
	}

	return (
		<code className={css(s.code)} {...props}>
			{children}
		</code>
	);
};

/**
 * HexColorRenderer - Renders hex color codes with visual swatches.
 *
 * This renderer:
 * 1. Preprocesses text to wrap bare hex codes (e.g., #0033A1) in backticks
 * 2. Provides a custom code component that renders the swatch
 *
 * Usage:
 * ```tsx
 * <AIAssistantDialog customRenderers={[HexColorRenderer]} />
 * ```
 */
export const HexColorRenderer: TextRenderer = {
	name: 'hexColor',
	preprocess: (text: string) => {
		// Wrap hex colors in backticks if they aren't already
		// Matches #RGB or #RRGGBB not followed by alphanumeric (to avoid partial matches)
		// Negative lookbehind (?<!`) ensures we don't double-wrap
		return text.replace(/(?<!`)#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})(?![0-9A-Fa-f])/g, '`$&`');
	},
	components: {
		code: ColorSwatchCode,
	},
};

const s = StyleSheet.create({
	code: {
		boxSizing: 'border-box',
		margin: 0,
		borderRadius: 4,
		backgroundColor: '#262626',
		color: '#e5e5e5',
		paddingLeft: 6,
		paddingRight: 6,
		paddingTop: 2,
		paddingBottom: 2,
		fontSize: '0.875rem',
		lineHeight: 1.43,
		fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
	},
	colorCode: {
		boxSizing: 'border-box',
		margin: 0,
		borderRadius: 4,
		backgroundColor: 'rgba(0, 0, 0, 0.05)',
		color: 'inherit',
		paddingLeft: 6,
		paddingRight: 6,
		paddingTop: 2,
		paddingBottom: 2,
		fontSize: '0.875rem',
		lineHeight: 1.43,
		fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
	},
	colorSwatch: {
		display: 'inline-block',
		width: 12,
		height: 12,
		borderRadius: 2,
		marginRight: 4,
		verticalAlign: 'middle',
		border: '1px solid rgba(0, 0, 0, 0.15)',
	},
});
